<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div>
  <b-message v-if="error" type="is-danger" has-icon icon-size="is-small" size="is-small">
    <h2> {{ $t('error') }} </h2>
    <p> {{ $t('unexpected-error-info-message') }} </p>
  </b-message>

  <b-tabs v-else v-model="activeTab">
    <b-tab-item :label="$t('image-summary')" v-if="image">
      <div class="image-details-wrapper">
        <table class="table">
          <tbody>
          <tr v-if="isPropDisplayed('overview')">
            <td class="prop-label">{{$t('overview')}}</td>
            <td class="prop-content" colspan="3">
              <image-thumbnail :image="image" :size="256" :key="`${image.id}-thumb-256`" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }"/>
            </td>
          </tr>
          <tr v-if="isPropDisplayed('description')">
            <td class="prop-label">{{$t('description')}}</td>
            <td class="prop-content" colspan="3">
              <cytomine-description :object="image" :canEdit="canEditImageMetadata" />
            </td>
          </tr>
          <tr v-if="isPropDisplayed('tags')">
            <td class="prop-label">{{$t('tags')}}</td>
            <td class="prop-content" colspan="3">
              <cytomine-tags :object="image" :canEdit="canEditImageMetadata" />
            </td>
          </tr>
          <tr v-if="isPropDisplayed('properties')">
            <td class="prop-label">{{$t('properties')}}</td>
            <td class="prop-content" colspan="3">
              <cytomine-properties
                  :object="image"
                  :canEdit="canEditImageMetadata"
              />
            </td>
          </tr>
          <tr v-if="isPropDisplayed('attached-files')">
            <td class="prop-label">{{$t('attached-files')}}</td>
            <td class="prop-content" colspan="3">
              <attached-files :object="image" :canEdit="canEditImageMetadata" />
            </td>
          </tr>
          <tr v-if="isPropDisplayed('original-filename')">
            <td class="prop-label">{{$t('originalFilename')}}</td>
            <td class="prop-content" colspan="3">
              {{image.originalFilename}}
            </td>
          </tr>
          <tr v-if="isPropDisplayed('format')">
            <td class="prop-label">{{$t('format')}}</td>
            <td class="prop-content format" colspan="3">
              {{image.contentType}}
            </td>
          </tr>
          <tr v-if="isPropDisplayed('vendor')">
            <td class="prop-label">{{$t('vendor')}}</td>
            <td class="prop-content" colspan="3">
              <img v-if="vendor" :src="vendor.imgPath" :alt="vendor.name" :title="vendor.name" class="vendor-img">
              <template v-else>{{$t('unknown')}}</template>
            </td>
          </tr>
          <tr v-if="isPropDisplayed('width') || isPropDisplayed('physicalSizeX')">
            <template v-if="isPropDisplayed('width')">
              <td class="prop-label">{{$t("image-width")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('physicalSizeX') ? 1 : 3">
                {{image.width}} {{$t("pixels")}}
                <template v-if="image.physicalSizeX">({{(image.width * image.physicalSizeX).toFixed(3)}} {{$t("um")}})</template>
              </td>
            </template>
            <template v-if="isPropDisplayed('physicalSizeX')">
              <td class="prop-label">{{$t("x-resolution")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('width') ? 1 : 3">
                <template v-if="image.physicalSizeX">{{image.physicalSizeX.toFixed(3)}} {{$t("um-per-pixel")}}</template>
                <template v-else>{{$t("unknown")}}</template>
              </td>
            </template>
          </tr>
          <tr v-if="isPropDisplayed('height') || isPropDisplayed('physicalSizeY')">
            <template v-if="isPropDisplayed('height')">
              <td class="prop-label">{{$t("image-height")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('physicalSizeY') ? 1 : 3">
                {{image.height}} {{$t("pixels")}}
                <template v-if="image.physicalSizeY">({{(image.height * image.physicalSizeY).toFixed(3)}} {{$t("um")}})</template>
              </td>
            </template>
            <template v-if="isPropDisplayed('physicalSizeY')">
              <td class="prop-label">{{$t("y-resolution")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('height') ? 1 : 3">
                <template v-if="image.physicalSizeY">{{image.physicalSizeY.toFixed(3)}} {{$t("um-per-pixel")}}</template>
                <template v-else>{{$t("unknown")}}</template>
              </td>
            </template>
          </tr>
          <tr v-if="isPropDisplayed('depth') || isPropDisplayed('physicalSizeZ')">
            <template v-if="isPropDisplayed('depth')">
              <td class="prop-label">{{$t("image-depth")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('physicalSizeZ') ? 1 : 3">
                {{$tc("count-slices", image.depth, {count: image.depth})}}
                <template v-if="image.physicalSizeZ">({{(image.depth * image.physicalSizeZ).toFixed(3)}} {{$t("um")}})</template>
              </td>
            </template>
            <template v-if="isPropDisplayed('physicalSizeZ')">
              <td class="prop-label">{{$t("z-resolution")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('depth') ? 1 : 3">
                <template v-if="image.physicalSizeZ">{{image.physicalSizeZ.toFixed(3)}} {{$t("um-per-slice")}}</template>
                <template v-else-if="image.depth < 2">-</template>
                <template v-else>{{$t("unknown")}}</template>
              </td>
            </template>
          </tr>
          <tr v-if="isPropDisplayed('time') || isPropDisplayed('fps')">
            <template v-if="isPropDisplayed('time')">
              <td class="prop-label">{{$t("image-time")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('fps') ? 1 : 3">
                {{$tc("count-frames", image.duration, {count: image.duration})}}
                <template v-if="image.fps && image.duration > 0">
                  ({{formatMinutesSeconds(image.duration / image.fps)}})
                </template>
              </td>
            </template>
            <template v-if="isPropDisplayed('fps')">
              <td class="prop-label">{{$t("frame-rate")}}</td>
              <td class="prop-content-half" :colspan="isPropDisplayed('time') ? 1 : 3">
                <template v-if="image.fps">{{image.fps.toFixed(3)}} {{$t("frame-per-second")}}</template>
                <template v-else-if="image.time < 2">-</template>
                <template v-else>{{$t("unknown")}}</template>
              </td>
            </template>
          </tr>
          <tr v-if="isPropDisplayed('channels')">
            <td class="prop-label">{{$t("image-channels")}}</td>
            <td class="prop-content" colspan="3">
              {{image.channels}} x {{image.samplePerPixel}}
            </td>
          </tr>
          <tr v-if="isPropDisplayed('size')">
            <td class="prop-label">{{$t('image-size')}}</td>
            <td class="prop-content" colspan="3">
              {{`${image.width} x ${image.height} ${$t('pixels')}`}}
            </td>
          </tr>
          <tr v-if="isPropDisplayed('magnification')">
            <td class="prop-label">{{$t('magnification')}}</td>
            <td class="prop-content" colspan="3">
              <template v-if="image.magnification">{{image.magnification}}</template>
              <template v-else>{{$t('unknown')}}</template>
            </td>
          </tr>
          <tr>
            <td class="prop-label">{{$t('actions')}}</td>
            <td class="prop-content" colspan="3">
              <div class="buttons are-small">
                <template v-if="canEditImageMetadata">
                  <button class="button" @click="isRenameModalActive = true">
                    {{$t('button-rename')}}
                  </button>
                </template>
              </div>
            </td>
          </tr>
          </tbody>
        </table>

        <rename-modal
            :title="$t('rename-image')"
            :currentName="image.originalFilename"
            :active.sync="isRenameModalActive"
            @rename="rename"
        />
      </div>
    </b-tab-item>


    <b-tab-item :label="$t('image-metadata')" v-if="image">
        <image-metadata
          :image="image"
          :show-macro="false"
        />
    </b-tab-item>


    <b-tab-item :label="$t('sample-preview')" v-if="file.thumbURL">
      <h2>
        {{$t('sample-preview-of', {filename: file.originalFilename})}}
      </h2>
      <image-thumbnail :url="file.thumbURL" :size="512" :key="file.thumbURL" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }" />
    </b-tab-item>


    <b-tab-item :label="$t('slide-preview')" v-if="image && image.macroURL">
      <h2>
        {{$t('slide-preview-of', {filename: file.originalFilename})}}
      </h2>
      <image-thumbnail :url="image.macroURL" :size="512" :key="image.macroURL" :macro="true" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }"/>
    </b-tab-item>


    <b-tab-item :label="$t('image-explore')" v-if="image" value="EXPLORE">
      <h2>
        {{$t('image-explore-of', {filename: file.originalFilename})}}
      </h2>
      <uploaded-file-details-viewer :image="image" v-if="activeTab === 'EXPLORE'"></uploaded-file-details-viewer>
    </b-tab-item>


    <b-tab-item :label="$t('file-tree')" v-if="currentUser.isDeveloper">
      <sl-vue-tree v-model="nodes" :allowMultiselect="false">
        <template #toggle="{node}">
          <template v-if="!node.isLeaf">
            <i :class="['tree-toggle', 'fas', node.isExpanded ? 'fa-angle-down' : 'fa-angle-right']"></i>
          </template>
        </template>

        <template #title="{node}">
          <p :class="{'target-element': node.data.id === file.id}">{{node.title}}</p>
        </template>

        <template #sidebar="{node}">
          <div class="filesize">
            {{filesize(node.data.size)}}
          </div>
          <div class="status">
            <uploaded-file-status :file="node.data" :iconOnly="true" />
          </div>
        </template>
      </sl-vue-tree>

      <template v-if="nbUploadedFiles > 10">
        <div class="level">
          <b-select v-model="nbPerPage" size="is-small" class="level-left">
            <option value="10">{{$t('count-per-page', {count: 10})}}</option>
            <option value="25">{{$t('count-per-page', {count: 25})}}</option>
            <option value="50">{{$t('count-per-page', {count: 50})}}</option>
            <option value="100">{{$t('count-per-page', {count: 100})}}</option>
          </b-select>

          <b-pagination
              class="level-right"
              :total="nbUploadedFiles"
              :current.sync="currentPage"
              size="is-small"
              :per-page="nbPerPage"
          />
        </div>
      </template>
    </b-tab-item>
  </b-tabs>
</div>
</template>

<script>
import SlVueTree from 'sl-vue-tree';
import {UploadedFile, UploadedFileCollection, AbstractImage, UploadedFileStatus as UFStatus} from 'cytomine-client';
import UploadedFileStatus from './UploadedFileStatus';
import CytomineDescription from '@/components/description/CytomineDescription';
import CytomineProperties from '@/components/property/CytomineProperties';
import CytomineTags from '@/components/tag/CytomineTags';
import AttachedFiles from '@/components/attached-file/AttachedFiles';
import MagnificationModal from '@/components/image/MagnificationModal';
import CalibrationModal from '@/components/image/CalibrationModal';
import RenameModal from '@/components/utils/RenameModal';
import ImageMetadataModal from '../image/ImageMetadataModal';
import ImageStatus from '../image/ImageStatus.vue';
import UploadedFileDetailsViewer from '@/components/storage/UploadedFileDetailsViewer';
import filesize from 'filesize';
import {appendShortTermToken} from '@/utils/token-utils.js';
import {get} from '@/utils/store-helpers.js';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import vendorFromFormat from '@/utils/vendor';
import {PropertyCollection} from 'cytomine-client';
import constants from '@/utils/constants.js';
import ImageMetadata from "@/components/image/ImageMetadata.vue";

export default {
  name: 'uploaded-file-details',
  components: {
    ImageMetadata,
    ImageThumbnail,
    SlVueTree,
    CytomineDescription,
    CytomineTags,
    CytomineProperties,
    AttachedFiles,
    MagnificationModal,
    CalibrationModal,
    RenameModal,
    ImageMetadataModal,
    UploadedFileStatus,
    UploadedFileDetailsViewer,
    vendorFromFormat,
    ImageStatus
  },
  props: {
    file: Object // WARNING: the root of the tree must be the file or its direct parent
  },
  data() {
    return {
      canEditImageMetadata : true,
      rootId: null,
      uploadedFiles: [],
      nodes: [],
      image: null,
      abstractImage: null,
      error: false,
      activeTab: 0,

      nbUploadedFiles: 0,
      currentPage: 1,
      nbPerPage: 10,

      isRenameModalActive: false,
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    currentUser: get('currentUser/user'),
    collection() {
      return new UploadedFileCollection({
        root: this.rootId,
        max: this.nbPerPage
      });
    },
    vendor() {
      return vendorFromFormat(this.image.contentType);
    },
  },
  watch: {
    async file() {
      this.findRoot();
      await Promise.all([this.fetchAbstractImage(), this.makeTree()]);
    },
    async currentPage() {
      this.findRoot();
      await this.makeTree();
    },
    async collection() {
      await this.makeTree();
    },
  },
  methods: {
    appendShortTermToken,
    isPropDisplayed(prop) {
      return true; // the current implementation is not able to hide data as we are outside of the project view
    },
    findRoot() {
      this.rootId = this.file.root || this.file.id;
    },
    async fetchAbstractImage() {
      if (this.file.image && (this.file.status === UFStatus.CONVERTED || this.file.status === UFStatus.DEPLOYED)) {
        try {
          this.image = await AbstractImage.fetch(this.file.image);
        }
        catch(error) {
          console.log(error);
          this.error = true;
        }
      }
    },
    async makeTree() {
      try {
        let data = (await this.collection.fetchPage(this.currentPage - 1));
        this.uploadedFiles = data.array;
        this.nbUploadedFiles = data.totalNbItems;
        this.nodes = (await this.createNodes(null));
      }
      catch(error) {
        console.log(error);
        this.error = true;
      }
    },
    async createNodes(idParent) {
      let directChildren = this.uploadedFiles.filter(file => file.parent === idParent);

      if (directChildren.length === 0 && idParent === null) {
        let missingIds = this.uploadedFiles[0].lTree.split('.');
        await Promise.all(missingIds.slice(0, missingIds.length - 1).map(async id => {
          this.uploadedFiles.unshift((await UploadedFile.fetch(id)));
        }));
        return this.createNodes(null);
      }

      return await Promise.all(directChildren.map(async file => {
        let children = await this.createNodes(file.id);
        return {
          title: file.originalFilename,
          isLeaf: children.length === 0,
          isDraggable: false,
          isExpanded: true,
          data: {downloadURL: file.downloadURL, ...file}, // data converted to object by sl-vue-tree => need to define downloadURL as property
          children
        };
      }));
    },
    filesize(size) {
      return filesize(size, {base: 10});
    },

    async rename(newName) {
      let oldName = this.image.originalFilename;
      try {
        this.image.originalFilename = newName;
        await this.image.save();
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-image-rename', {imageName: this.image.originalFilename})
        });
        this.$emit('update');
      }
      catch(error) {
        this.image.originalFilename = oldName;
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-image-rename', {imageName: oldName})
        });
      }
      this.isRenameModalActive = false;
    },
  },
  async created() {
    this.findRoot();
    await this.fetchAbstractImage();
  }
};
</script>

<style scoped>
.target-element {
  font-weight: 600;
  min-width: 0; /* to allow correct handling of overflow-wrap */
}

.filesize {
  width: 8em;
  margin-left: 1em;
}

.status {
  width: 5em;
}

.preview {
  width: 20em;
}

.buttons {
  margin-right: 2em;
  margin-bottom: 0 !important;
}

.buttons .button {
  margin-bottom: 0.5em !important;
  margin-top: 0.2em !important;
}

h2:not(:first-child) {
  margin-top: 1em;
  /*border-bottom: 2px solid #ddd;*/
}

h2 .button {
  position: relative;
  bottom: 3px;
  margin-left: 0.8em;
  text-transform: none;
}

::v-deep .sl-vue-tree-sidebar {
  display: flex;
  align-items: top;
  justify-content: flex-end;
}

::v-deep .sl-vue-tree-gap {
  border: 0 dotted #bbb;
  border-left-width: 1px;
  position: relative;
  right: 0.95em;
  bottom: 1.25em;
}

::v-deep .sl-vue-tree-toggle {
  background: #fafafa;
  z-index: 1;
}

::v-deep .sl-vue-tree-gap:nth-last-child(3) {
  border-width: 0 0 1px 1px !important;
}

::v-deep ul.pagination-list {
  justify-content: flex-end;
}

.level {
  padding-bottom: 0.5rem !important;
}


.table {
  background: none;
  position: relative;
  margin-bottom: 0 !important;
}

td.prop-label {
  white-space: nowrap;
  font-weight: 600;
}

td.prop-content {
  width: 100%;
}

td.prop-content-half {
  width: 50%;
}

.format {
  text-transform: uppercase;
}

.vendor-img {
  max-height: 4rem;
  max-width: 12rem;
}
</style>
